import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Globe, Sparkles, Loader2, ShieldCheck, Wand2, Check, MessageSquare, Clock, ArrowRight, Mail, X, ArrowLeft, LogIn,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useOnboardingAuth } from "../contexts/OnboardingAuthContext";
import HideGlobalWidget from "../components/HideGlobalWidget";
import { LockedDraftTeaser } from "../components/LockedDraftTeaser";
import { API_URL } from "../utils/api";
import { captureAttribution, getAttribution } from "../utils/attribution";
import { AiDisclosureNotice } from "../components/chatbot/AiDisclosureNotice";

const STATUS_META = {
  analyzing: { label: "Analyserar...", cls: "bg-blue-500/15 text-blue-200 border-blue-400/30" },
  draft: { label: "Utkast", cls: "bg-white/10 text-white/70 border-white/15" },
  trial_requested: { label: "Trial begärd", cls: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
  approved: { label: "Godkänd", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  promoted: { label: "Aktiverad", cls: "bg-[#7FD4DE]/15 text-[#7FD4DE] border-[#7FD4DE]/30" },
  rejected: { label: "Avvisad", cls: "bg-red-500/15 text-red-200 border-red-400/30" },
};

// Persist the URL across the Google-OAuth round trip so we can resume
// analysis once the user returns authenticated.
const PENDING_URL_KEY = "elido_onboarding_pending_url";
// Persist a pending anon→user claim across the Google OAuth round trip.
// Set when the user starts Google login from the LockedDraftTeaser so
// we can attach the anon draft to the new account on return.
const PENDING_CLAIM_KEY = "elido_onboarding_pending_claim";
const ANON_TOKEN_KEY = "elido_onboarding_anon_token";

function _getOrCreateAnonToken() {
  try {
    const existing = localStorage.getItem(ANON_TOKEN_KEY);
    if (existing) return existing;
    const fresh = (crypto?.randomUUID?.() ||
      `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
    localStorage.setItem(ANON_TOKEN_KEY, fresh);
    return fresh;
  } catch (_) {
    return `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function OnboardingCreatePage() {
  const navigate = useNavigate();
  const { isAuthed, user, loading, authedFetch, logout, applyToken, requestCode, verifyCode } = useOnboardingAuth();

  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [authGate, setAuthGate] = useState(null); // null | "open"
  const [myDrafts, setMyDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const oauthHandled = useRef(false);

  // ── Anon teaser flow state ──────────────────────────────────────────
  // teaserPhase: null (idle) | 'analyzing' (skeleton) | 'ready' (LockedDraftTeaser)
  const [teaserPhase, setTeaserPhase] = useState(null);
  const [teaserDraft, setTeaserDraft] = useState(null);
  const [teaserAnonToken, setTeaserAnonToken] = useState("");
  const teaserPollRef = useRef(null);

  // ── Discover whether Google OAuth is configured server-side ───────────
  useEffect(() => {
    captureAttribution();
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/onboarding/auth/google/config`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setGoogleEnabled(!!data.enabled);
      } catch (_) { /* leave disabled */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Show OAuth errors surfaced via ?google_oauth_error=...  ────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("google_oauth_error");
    if (!err) return;
    const map = {
      cancelled: "Inloggningen avbröts",
      invalid_state: "Säkerhetstoken matchar inte — försök igen",
      expired_state: "Inloggningssessionen gick ut — försök igen",
      email_not_verified: "Din Google-e-post är inte verifierad",
      missing_email: "Google-svaret saknade e-post",
      token_exchange_failed: "Kunde inte byta token mot Google",
      invalid_id_token: "Ogiltig inloggningstoken från Google",
      missing_id_token: "Saknade inloggningstoken från Google",
      missing_params: "Inloggningssvaret saknade parametrar",
      misconfigured: "Google OAuth är felkonfigurerad",
      disabled: "Google-inloggning är inte aktiverad",
    };
    toast.error(map[err] || `Google-inloggning misslyckades (${err})`);
    // Strip the param so it doesn't re-fire on refresh
    params.delete("google_oauth_error");
    const next = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState({}, "", next);
  }, []);

  // ── Handle return from self-hosted Google OAuth: #google_oauth_token=... ─
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("google_oauth_token=") || oauthHandled.current) return;
    oauthHandled.current = true;

    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const token = params.get("google_oauth_token") || "";
    const email = params.get("email") || "";
    if (!token) return;

    // Strip the fragment from the URL immediately so a refresh doesn't re-trigger
    window.history.replaceState({}, "", window.location.pathname + window.location.search);

    (async () => {
      setSubmitting(true);
      try {
        applyToken(token, email);
        toast.success("Inloggad med Google");
        // Resume analysis if there was a pending URL
        const pendingUrl = (() => {
          try { return localStorage.getItem(PENDING_URL_KEY) || ""; } catch { return ""; }
        })();
        if (pendingUrl) {
          try { localStorage.removeItem(PENDING_URL_KEY); } catch (_) { /* ignore */ }
          setUrl(pendingUrl);
          await analyzeAfterAuth(pendingUrl);
        }
      } catch (e) {
        toast.error(e.message || "Google-inloggning misslyckades");
      } finally {
        setSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load drafts only when the user is logged in ────────────────────────
  useEffect(() => {
    if (!isAuthed) { setMyDrafts([]); return; }
    let cancelled = false;
    setDraftsLoading(true);
    (async () => {
      try {
        const res = await authedFetch("/api/onboarding/me/drafts");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMyDrafts(data.drafts || []);
      } finally {
        if (!cancelled) setDraftsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed, authedFetch]);

  const analyzeAfterAuth = async (urlValue) => {
    const trimmed = (urlValue || "").trim();
    if (!trimmed) return;
    const res = await authedFetch("/api/onboarding/analyze", {
      method: "POST",
      body: JSON.stringify({ url: trimmed, attribution: getAttribution() }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(data.detail || "Kunde inte skapa utkast");
      return;
    }
    navigate(`/skapa-ai-agent/utkast/${data.draft_id}`);
  };

  // ── Anon teaser flow ────────────────────────────────────────────────
  // For visitors who are NOT logged in, kick off /analyze-anon and poll
  // the resulting draft. Show the LockedDraftTeaser as soon as the
  // draft leaves the "analyzing" state. After login the teaser claims
  // the anon draft and navigates to the full /utkast/<id> view.
  const startAnonAnalysis = async (urlValue) => {
    const trimmed = (urlValue || "").trim();
    if (!trimmed) return;
    const anonToken = _getOrCreateAnonToken();
    setTeaserAnonToken(anonToken);
    setTeaserPhase("analyzing");
    try {
      const res = await fetch(`${API_URL}/api/onboarding/analyze-anon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: trimmed, anon_token: anonToken, attribution: getAttribution() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Kunde inte starta analysen");
        setTeaserPhase(null);
        return;
      }
      pollAnonDraft(data.draft_id, anonToken);
    } catch (err) {
      toast.error("Nätverksfel — försök igen");
      setTeaserPhase(null);
    }
  };

  const pollAnonDraft = (draftId, anonToken) => {
    if (teaserPollRef.current) clearInterval(teaserPollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~60s @ 1.5s

    const tick = async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_URL}/api/onboarding/draft-anon/${draftId}`, {
          headers: { "X-Anon-Token": anonToken },
        });
        if (!res.ok) {
          if (attempts >= MAX_ATTEMPTS) {
            clearInterval(teaserPollRef.current);
            teaserPollRef.current = null;
            toast.error("Det tog lite längre tid än väntat — prova igen.");
            setTeaserPhase(null);
          }
          return;
        }
        const data = await res.json();
        const draft = data.draft;
        if (draft && draft.status && draft.status !== "analyzing") {
          clearInterval(teaserPollRef.current);
          teaserPollRef.current = null;
          setTeaserDraft(draft);
          setTeaserPhase("ready");
        } else if (attempts >= MAX_ATTEMPTS) {
          clearInterval(teaserPollRef.current);
          teaserPollRef.current = null;
          // Still analyzing — show what we have anyway
          setTeaserDraft(draft || { draft_id: draftId });
          setTeaserPhase("ready");
        }
      } catch (_) {
        /* transient — keep polling */
      }
    };

    // first poll immediate, then interval
    tick();
    teaserPollRef.current = setInterval(tick, 1500);
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (teaserPollRef.current) clearInterval(teaserPollRef.current);
    };
  }, []);

  const resetTeaser = () => {
    if (teaserPollRef.current) clearInterval(teaserPollRef.current);
    teaserPollRef.current = null;
    setTeaserPhase(null);
    setTeaserDraft(null);
    setUrl("");
  };

  // After successful login from the teaser: claim the anon draft and
  // navigate to the full /utkast/<id> view.
  const handleTeaserAuthSuccess = async (draftId) => {
    toast.success("Inloggad — utkastet är ditt nu");
    navigate(`/skapa-ai-agent/utkast/${draftId}`);
  };

  // ── Resume claim after Google OAuth round-trip ──────────────────────
  // The teaser's Google button persists PENDING_CLAIM_KEY before
  // redirecting; on return we read it, POST /claim, then navigate.
  useEffect(() => {
    if (!isAuthed) return;
    let pending;
    try { pending = JSON.parse(localStorage.getItem(PENDING_CLAIM_KEY) || "null"); } catch (_) { pending = null; }
    if (!pending || !pending.draft_id || !pending.anon_token) return;
    (async () => {
      try {
        const token = localStorage.getItem("elido_onboarding_jwt") || "";
        const res = await fetch(`${API_URL}/api/onboarding/draft-anon/${pending.draft_id}/claim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Anon-Token": pending.anon_token,
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          toast.success("Utkastet är ditt nu");
          try { localStorage.removeItem(PENDING_CLAIM_KEY); } catch (_) { /* ignore */ }
          navigate(`/skapa-ai-agent/utkast/${pending.draft_id}`);
        }
      } catch (_) { /* silent — user can still browse drafts list */ }
    })();
  }, [isAuthed, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) { toast.error("Skriv in din hemsida"); return; }
    if (!_looksLikeValidUrl(trimmed)) {
      toast.error("Ange en riktig webbadress, t.ex. https://foretag.se");
      return;
    }
    // For UNAUTHENTICATED visitors: run the anonymous teaser flow.
    // The draft is generated immediately, blurred, and the user
    // unlocks it by logging in (magic-link or Google) from the
    // LockedDraftTeaser. This replaces the previous "login wall first"
    // approach and lets visitors see the value before committing.
    if (!isAuthed) {
      await startAnonAnalysis(trimmed);
      return;
    }
    setSubmitting(true);
    try {
      await analyzeAfterAuth(trimmed);
    } catch (err) {
      toast.error("Nätverksfel — försök igen");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading splash only while auth context hydrates from localStorage
  if (loading && !isAuthed && !window.location.hash.includes("session_id=")) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30]">
        <Loader2 className="w-6 h-6 text-white/60 animate-spin" />
      </main>
    );
  }

  // ── Anon teaser flow rendering ──────────────────────────────────────
  if (teaserPhase === "analyzing") {
    return (
      <main
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white px-5"
        data-testid="onboarding-anon-analyzing"
      >
        <HideGlobalWidget />
        <div className="w-full max-w-md text-center">
          <div className="relative mx-auto w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] opacity-30 animate-ping" />
            <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#3A8A98] to-[#7FD4DE] flex items-center justify-center shadow-2xl shadow-[#3A8A98]/40">
              <Wand2 className="w-9 h-9 text-[#0F2A30]" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Bygger din AI-agent…
          </h1>
          <p className="mt-3 text-sm text-white/65 leading-relaxed">
            Läser av din hemsida, identifierar tonläge och bygger ett första utkast med vanliga frågor.
          </p>
          <div className="mt-8 space-y-2 text-left">
            {[
              "Hämtar publik info från din sida",
              "Identifierar bransch och tjänster",
              "Skapar agentens personlighet",
              "Genererar vanliga kundfrågor",
            ].map((step, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-sm text-white/70"
                style={{ animationDelay: `${i * 250}ms` }}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7FD4DE]" />
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (teaserPhase === "ready" && teaserDraft) {
    return (
      <>
        <HideGlobalWidget />
        <LockedDraftTeaser
          draft={teaserDraft}
          anonToken={teaserAnonToken}
          googleEnabled={googleEnabled}
          onAuthSuccess={handleTeaserAuthSuccess}
          onReset={resetTeaser}
          requestCode={requestCode}
          verifyCode={verifyCode}
        />
      </>
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-[#0F2A30] via-[#15414A] to-[#0F2A30] text-white"
      data-testid="onboarding-create-page"
    >
      <Helmet>
        <title>Skapa en AI-agent för din hemsida — utkast på 60 sek | Elido</title>
        <meta
          name="description"
          content="Ange din hemsida så bygger Elido Nexus ett första utkast åt dig — företagsinfo, tjänster, tonläge och vanliga frågor. Testa AI-agenten direkt i webbläsaren. 14 dagar gratis, ingen bindning."
        />
        <meta property="og:title" content="Skapa en AI-agent för din hemsida — utkast på 60 sek | Elido" />
        <meta
          property="og:description"
          content="Ange din hemsida så bygger Elido Nexus ett första utkast åt dig — företagsinfo, tjänster, tonläge och vanliga frågor. Testa AI-agenten direkt i webbläsaren. 14 dagar gratis, ingen bindning."
        />
        <link rel="canonical" href="https://elido.nu/skapa-ai-agent" />
      </Helmet>
      <HideGlobalWidget />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative">
        {/* Top bar */}
        <nav className="px-5 sm:px-10 py-5 flex items-center justify-between text-xs">
          <a
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="Gå till startsidan"
            data-testid="onboarding-logo-home"
          >
            <img src="/elido-logo.png" alt="Elido" className="h-7 w-auto" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 hidden sm:inline">
              Onboarding
            </span>
          </a>
          <div className="flex items-center gap-3 text-white/70">
            <a
              href="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-white/30 hover:bg-white/[0.04] transition-colors text-white/80 text-[12px]"
              data-testid="onboarding-back-home"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Tillbaks
            </a>
            {isAuthed ? (
              <>
                <span data-testid="onboarding-user-email" className="hidden sm:inline">{user?.email}</span>
                <button
                  onClick={logout}
                  className="hover:text-white transition-colors"
                  data-testid="onboarding-logout-btn"
                >
                  Logga ut
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setAuthGate("open")}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#7FD4DE] hover:bg-[#A8E6D8] text-[#0F2A30] text-[12px] font-semibold transition-colors"
                data-testid="onboarding-header-login-btn"
              >
                <LogIn className="w-3.5 h-3.5" />
                Logga in
              </button>
            )}
          </div>
        </nav>

        {/* Hero */}
        <section className="px-5 sm:px-10 pt-12 pb-16 sm:pt-20 sm:pb-24 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] bg-[#3A8A98]/15 border border-[#3A8A98]/30 rounded-full px-3 py-1.5 mb-6" data-testid="onboarding-hero-badge">
                <Sparkles className="w-3 h-3" />
                14 dagars gratis test
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                Skapa en{" "}
                <span className="bg-gradient-to-r from-[#7FD4DE] via-[#A8E6D8] to-[#7FD4DE] bg-clip-text text-transparent">
                  AI-agent
                </span>{" "}
                för din hemsida
              </h1>
              <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
                Ange din hemsida så bygger Elido Nexus ett första utkast åt dig — med företagsinfo, tjänster, tonläge och vanliga frågor.
              </p>
              <p className="mt-3 text-base text-white/70 leading-relaxed max-w-xl">
                Du kan testa AI-agenten direkt i webbläsaren innan något läggs live.
              </p>

              <form
                onSubmit={handleSubmit}
                className="mt-10 bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-2xl p-5 sm:p-6"
              >
                <label htmlFor="ob-url" className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/60">
                  Webbadress
                </label>
                <div className="relative mt-3">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                  <Input
                    id="ob-url"
                    type="text"
                    autoFocus
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://erfirma.se"
                    className="pl-11 h-14 text-base bg-[#0a1f24] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                    data-testid="onboarding-url-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  size="lg"
                  className="mt-4 w-full sm:w-auto bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl shadow-lg shadow-[#3A8A98]/20"
                  data-testid="onboarding-create-draft-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Skapa AI-agent
                </Button>
                <p className="text-xs text-white/40 mt-4 leading-relaxed">
                  Vi hämtar bara publik information från din hemsida och bygger ett utkast direkt — du loggar in när du vill spara eller aktivera det.
                </p>
              </form>
            </div>

            <div className="relative hidden lg:block">
              <ChatPreviewMockup />
            </div>
          </div>
        </section>

        {/* Feature strip */}
        <section className="border-t border-white/[0.06] bg-white/[0.02]">
          <div className="max-w-6xl mx-auto px-5 sm:px-10 py-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { num: "01", icon: Sparkles, title: "AI:n läser din hemsida", body: "Elido Nexus hämtar publik information från din hemsida och skapar ett första utkast baserat på era tjänster, ert språk och era vanliga kundfrågor." },
              { num: "02", icon: MessageSquare, title: "Testa direkt i webbläsaren", body: "Prata med AI-agenten i en trygg sandbox. Justera svar, ton, frågor och flöden innan något används på riktigt." },
              { num: "03", icon: ShieldCheck, title: "Gå live efter granskning", body: "Inget aktiveras på er hemsida automatiskt. När ni är nöjda granskar vi utkastet och skickar en enkel kodrad för installation." },
            ].map((c, i) => (
              <div key={i} className="group">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono text-[11px] text-white/30">{c.num}</span>
                  <c.icon className="w-4 h-4 text-[#7FD4DE]" />
                </div>
                <div className="font-semibold text-white text-base">{c.title}</div>
                <div className="text-sm text-white/60 mt-1 leading-relaxed">{c.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* My drafts (only when user has any) */}
        {isAuthed && !draftsLoading && myDrafts.length > 0 && (
          <section className="border-t border-white/[0.06] bg-white/[0.02]">
            <div className="max-w-6xl mx-auto px-5 sm:px-10 py-12">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#7FD4DE]" />
                  Dina utkast
                </h2>
                <span className="text-xs text-white/40">{myDrafts.length} st</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="onboarding-my-drafts">
                {myDrafts.map((d) => {
                  const meta = STATUS_META[d.status] || STATUS_META.draft;
                  return (
                    <button
                      key={d.draft_id}
                      onClick={() => navigate(`/skapa-ai-agent/utkast/${d.draft_id}`)}
                      className="group text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 rounded-xl p-4 transition-all"
                      data-testid={`onboarding-draft-card-${d.draft_id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className={`text-[9px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${meta.cls}`}>
                          {meta.label}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-white/30 group-hover:text-white/80 transition-colors" />
                      </div>
                      <div className="text-sm font-semibold text-white truncate">
                        {d.company_name || _hostnameFromUrl(d.website_url) || "Okänt företag"}
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5 truncate">
                        {d.detected_industry || _hostnameFromUrl(d.website_url) || ""}
                      </div>
                      <div className="text-[10px] text-white/30 mt-2">
                        Senast: {d.updated_at ? new Date(d.updated_at).toLocaleDateString("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        <footer className="px-5 sm:px-10 py-8 text-center text-[11px] text-white/30">
          © {new Date().getFullYear()} Elido AB. Allt utkast hanteras enligt vår integritetspolicy.
        </footer>
      </div>

      {authGate && (
        <AuthGateModal
          urlValue={url}
          googleEnabled={googleEnabled}
          onClose={() => setAuthGate(null)}
          onCodeLogin={async (email, code) => {
            await verifyCode(email, code);
            setAuthGate(null);
            await analyzeAfterAuth(url);
          }}
          requestCode={requestCode}
        />
      )}
    </main>
  );
}

// ─ Auth gate modal (Google primary if enabled, e-post otherwise) ────────
function AuthGateModal({ urlValue, googleEnabled, onClose, onCodeLogin, requestCode }) {
  const [mode, setMode] = useState(googleEnabled ? "choose" : "email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const triggerGoogle = () => {
    // Persist the URL so we can resume analysis after the OAuth round-trip.
    try {
      if (urlValue && urlValue.trim()) {
        localStorage.setItem(PENDING_URL_KEY, urlValue.trim());
      }
    } catch (_) { /* ignore */ }
    // Self-hosted OAuth: full-page redirect to our backend, which redirects to Google.
    const returnUrl = "/skapa-ai-agent";
    window.location.href = `${API_URL}/api/onboarding/auth/google/start?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { toast.error("Ange e-post"); return; }
    setBusy(true);
    try {
      await requestCode(email.trim());
      toast.success("Vi skickade en kod till din e-post");
      setMode("code");
    } catch (err) {
      toast.error(err.message || "Kunde inte skicka kod");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) { toast.error("Ange koden"); return; }
    setBusy(true);
    try {
      await onCodeLogin(email.trim(), code.trim());
    } catch (err) {
      toast.error(err.message || "Fel kod");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="onboarding-auth-gate"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-[#0F2A30] to-[#0a1f24] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/40 hover:text-white/80 p-1.5 rounded-lg hover:bg-white/5"
          aria-label="Stäng"
          data-testid="onboarding-auth-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-7 pt-8 pb-2 text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#7FD4DE] mb-3">
            <Wand2 className="w-3 h-3" /> Spara utkastet
          </div>
          <h2 className="text-xl sm:text-2xl font-bold leading-tight">
            Sista steget innan vi bygger ditt utkast
          </h2>
          <p className="mt-2 text-sm text-white/60 leading-relaxed">
            {urlValue ? (<>Vi sparar utkastet på ditt konto så du kan komma tillbaka och redigera det.</>) : "Logga in för att spara utkastet."}
          </p>
        </div>

        <div className="p-7 pt-5">
          {mode === "choose" && googleEnabled && (
            <div className="space-y-3">
              <button
                onClick={triggerGoogle}
                className="w-full flex items-center justify-center gap-2.5 bg-white text-slate-900 font-semibold rounded-xl py-3 hover:bg-slate-50 transition-colors shadow-md"
                data-testid="onboarding-google-btn"
              >
                <GoogleIcon />
                <span>Fortsätt med Google</span>
              </button>

              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-wider text-white/40">eller</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <button
                onClick={() => setMode("email")}
                className="w-full flex items-center justify-center gap-2 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-white/85 rounded-xl py-3 transition-colors"
                data-testid="onboarding-email-btn"
              >
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Använd e-post & kod</span>
              </button>

              <p className="text-[11px] text-white/35 text-center pt-2 leading-relaxed">
                Inga lösenord. Du kan komma tillbaka när som helst för att fortsätta.
              </p>
            </div>
          )}

          {mode === "email" && (
            <form onSubmit={sendCode} className="space-y-3" data-testid="onboarding-email-form">
              <label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-white/50">E-post</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                <Input
                  autoFocus
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ditt.namn@exempel.se"
                  className="pl-10 h-12 bg-[#0a1f24] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                  data-testid="onboarding-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                data-testid="onboarding-send-code-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Skicka kod
              </Button>
              {googleEnabled && (
                <button
                  type="button"
                  onClick={() => setMode("choose")}
                  className="block text-xs text-white/50 hover:text-white/80 mx-auto"
                >
                  ← Tillbaka
                </button>
              )}
            </form>
          )}

          {mode === "code" && (
            <form onSubmit={submitCode} className="space-y-3" data-testid="onboarding-code-form">
              <p className="text-[12px] text-white/60">Vi skickade en 6-siffrig kod till <strong className="text-white">{email}</strong>.</p>
              <Input
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="h-14 text-2xl text-center tracking-[0.5em] font-mono bg-[#0a1f24] border-white/10 text-white placeholder:text-white/20 focus-visible:ring-[#7FD4DE]/50 focus-visible:border-[#7FD4DE]/60 rounded-xl"
                data-testid="onboarding-code-input"
              />
              <Button
                type="submit"
                disabled={busy || code.length < 6}
                className="w-full bg-gradient-to-r from-[#3A8A98] to-[#7FD4DE] hover:from-[#2c7280] hover:to-[#6cc4cf] text-[#0F2A30] font-semibold rounded-xl"
                data-testid="onboarding-verify-code-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Logga in & skapa utkast
              </Button>
              <button
                type="button"
                onClick={() => setMode("email")}
                className="block text-xs text-white/50 hover:text-white/80 mx-auto"
              >
                ← Begär en ny kod
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.7 6.1 29.6 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.3-.13-2.6-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.7 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4c-2 1.4-4.6 2.3-7.6 2.3-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 3.9-3.9 5.1l6.6 5.4C42.7 35.5 44 30.1 44 24c0-1.3-.13-2.6-.4-3.5z"/>
    </svg>
  );
}

function _hostnameFromUrl(url) {
  if (!url) return null;
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch (e) { return null; }
}

// Frontend mirror of backend `looks_like_valid_url`. Catches obviously
// invalid input ("ko", "abc def") locally so we don't roundtrip a 400 to
// the user. Backend remains the source of truth.
function _looksLikeValidUrl(value) {
  if (!value || !value.trim()) return false;
  let v = value.trim();
  if (!/^https?:\/\//i.test(v)) v = "https://" + v;
  let host;
  try { host = new URL(v).hostname; } catch (_e) { return false; }
  if (!host || host.length > 253) return false;
  if (!host.includes(".")) return false;
  // Allow IDN/Swedish chars and digits/hyphens; require a letter in the TLD.
  const labelRe = /^[\w-]+$/u;
  const labels = host.split(".");
  if (labels.some((l) => !l || l.length > 63 || l.startsWith("-") || l.endsWith("-") || !labelRe.test(l))) {
    return false;
  }
  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/[^\W\d_]/u.test(tld)) return false;
  return true;
}

function ChatPreviewMockup() {
  return (
    <div className="relative">
      <div className="absolute -inset-8 bg-gradient-to-br from-[#3A8A98]/30 via-transparent to-[#7FD4DE]/20 blur-3xl pointer-events-none" />
      <div className="relative bg-[#0a1f24]/60 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/60" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/60" />
          <span className="ml-3 text-[10px] text-white/30 font-mono">erfirma.se</span>
        </div>
        <div className="p-6 space-y-3">
          <div className="h-2 w-2/3 bg-white/[0.06] rounded" />
          <div className="h-2 w-1/2 bg-white/[0.04] rounded" />
          <div className="grid grid-cols-3 gap-2 mt-4">
            <div className="h-12 bg-white/[0.04] rounded" />
            <div className="h-12 bg-white/[0.04] rounded" />
            <div className="h-12 bg-white/[0.04] rounded" />
          </div>
          <div className="mt-6 bg-white rounded-xl shadow-xl p-4 text-slate-900">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#3A8A98] to-[#255E69] flex items-center justify-center">
                <MessageSquare className="w-3 h-3 text-white" />
              </div>
              <div>
                <div className="text-xs font-semibold">Kundservice</div>
                <div className="text-[10px] text-emerald-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online
                </div>
              </div>
            </div>
            <AiDisclosureNotice className="mx-0 mt-3 mb-2" />
            <div className="space-y-2">
              <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 text-xs leading-relaxed max-w-[85%]">
                Hej! Vad kan vi hjälpa dig med idag?
              </div>
              <div className="bg-[#255E69] text-white rounded-2xl rounded-br-sm px-3 py-2 text-xs leading-relaxed max-w-[85%] ml-auto">
                Vad kostar en flyttstädning av 2 rok?
              </div>
              <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 text-xs leading-relaxed max-w-[85%]">
                För 2 rok i Stockholm är priset från 1 950 kr. Vilken vecka önskar du städningen?
              </div>
            </div>
            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
              {["Boka tid", "Priser", "Vad gör ni?"].map((t, i) => (
                <span key={i} className="text-[10px] text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl px-4 py-3 text-slate-900">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          <div>
            <div className="text-xs font-semibold">Lead-mejl skickas direkt</div>
            <div className="text-[10px] text-slate-500">När en kund lämnar telefon eller mejl</div>
          </div>
        </div>
      </div>
    </div>
  );
}
